import { IconButton } from "@design";
import { Menu } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { type MobileHeaderSlot, MobileHeaderSlotContext } from "@/components/mobileHeaderSlot";
import { Logo } from "./Logo";
import { MobileDrawer } from "./MobileDrawer";

/**
 * TEMPORARY mobile shell (below md / 768px) until anthm-design ships a mobile
 * AppShell/SidebarNav. A top bar with a hamburger that opens the regular
 * SidebarNav in a drawer. Replace wholesale when the real design lands.
 *
 * Pages below a top-level destination put their breadcrumb in the top bar
 * (PageBreadcrumbs), in place of the logo.
 */
export function MobileShell({
  banner,
  nav,
  children,
}: Readonly<{ banner: ReactNode; nav: (close: () => void) => ReactNode; children: ReactNode }>) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { pathname } = useLocation();
  const { t } = useTranslation("shell");
  const [slotElement, setSlotElement] = useState<HTMLDivElement | null>(null);
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => n - 1);
  }, []);
  const slot = useMemo<MobileHeaderSlot>(() => ({ element: slotElement, claim }), [slotElement, claim]);

  // Navigating (any nav entry) closes the drawer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="flex h-dvh flex-col bg-surface-base">
      {banner}
      <header className="flex shrink-0 items-center gap-2 border-b border-border-subtle bg-surface-sidebar px-2 pt-[env(safe-area-inset-top)] h-[calc(3.5rem+env(safe-area-inset-top))]">
        <IconButton icon={<Menu />} aria-label={t("nav.open")} aria-expanded={open} onClick={() => setOpen(true)} />
        {claims === 0 && <Logo />}
        <div ref={setSlotElement} className="flex min-w-0 flex-1 items-center gap-2 pr-1" />
      </header>
      <MobileHeaderSlotContext.Provider value={slot}>
        {/* The page scrolls inside <main>, so <main> must be keyboard-focusable (WCAG 2.1.1, axe
            scrollable-region-focusable): a read-only page has no controls in it now that the
            breadcrumb lives in the top bar. */}
        <main
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-reachable
          tabIndex={0} // NOSONAR typescript:S6845 a scrollable region must be keyboard-reachable (see above)
          className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]"
        >
          {children}
        </main>
      </MobileHeaderSlotContext.Provider>
      <MobileDrawer open={open} onClose={close}>
        {nav(close)}
      </MobileDrawer>
    </div>
  );
}
